// frontend/src/components/ClientList.js
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import api from '../api';
import { exportClients } from '../utils/exportExcel';

const EMPTY_FORM = {
  nom: '', prenom: '', entreprise: '', email: '',
  telephone: '', adresse: '', code_postal: '', ville: ''
};

// ✅ Composant icône de tri
const SortIcon = ({ field, sortField, sortDir }) => {
  if (sortField !== field) return <span className="text-muted ms-1">↕</span>;
  return <span className="ms-1">{sortDir === 'asc' ? '↑' : '↓'}</span>;
};

function ClientList({ onClientChange }) {
  const [clients, setClients]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [showModal, setShowModal]           = useState(false);
  const [showConfirm, setShowConfirm]       = useState(false);
  const [editClient, setEditClient]         = useState(null);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [form, setForm]                     = useState(EMPTY_FORM);
  const [submitting, setSubmitting]         = useState(false);
  const [search, setSearch]                 = useState('');
  const [sortField, setSortField]           = useState('nom');
  const [sortDir, setSortDir]               = useState('asc');

  // ✅ Tri
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // ✅ Clients triés
  const sortedClients = [...clients].sort((a, b) => {
    let valA = a[sortField] || '';
    let valB = b[sortField] || '';
    if (typeof valA === 'string') valA = valA.toLowerCase();
    if (typeof valB === 'string') valB = valB.toLowerCase();
    if (valA < valB) return sortDir === 'asc' ? -1 : 1;
    if (valA > valB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // ✅ per_page=100 pour afficher tous les clients
      const params = search
        ? `?search=${encodeURIComponent(search)}&per_page=100`
        : '?per_page=100';
      const response = await api.get(`/clients${params}`);
      const data = response.data.clients || response.data;
      setClients(data);
      if (onClientChange) onClientChange(data.length);
    } catch (err) {
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const openCreateModal = () => {
    setEditClient(null);
    setForm(EMPTY_FORM);
    setError(null);
    setShowModal(true);
  };

  const openEditModal = (client) => {
    setEditClient(client);
    setForm({
      nom:         client.nom         || '',
      prenom:      client.prenom      || '',
      entreprise:  client.entreprise  || '',
      email:       client.email       || '',
      telephone:   client.telephone   || '',
      adresse:     client.adresse     || '',
      code_postal: client.code_postal || '',
      ville:       client.ville       || ''
    });
    setError(null);
    setShowModal(true);
  };

  const openConfirmDelete = (client) => {
    setClientToDelete(client);
    setShowConfirm(true);
  };

  const handleField = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (editClient) {
        await api.put(`/clients/${editClient.id}`, form);
      } else {
        await api.post('/clients', form);
      }
      setShowModal(false);
      await fetchClients();
    } catch (err) {
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/clients/${clientToDelete.id}`);
      setShowConfirm(false);
      setClientToDelete(null);
      await fetchClients();
    } catch (err) {
      setShowConfirm(false);
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
        <p className="mt-2">Chargement des clients...</p>
      </div>
    );
  }

  return (
    <>
      {error && (
        <Alert variant="danger" className="mt-3" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Barre d'outils */}
      <div className="d-flex justify-content-between align-items-center mb-3 mt-4">
        <div className="d-flex align-items-center gap-3">
          <h4 className="mb-0">Liste des clients ({clients.length})</h4>
          <Form.Control
            type="text"
            placeholder="🔍 Rechercher..."
            size="sm"
            style={{ width: '200px' }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Button variant="outline-success" className="me-2" onClick={() => exportClients(clients)}>📊 Excel</Button>
        <Button variant="primary" onClick={openCreateModal}>
          + Nouveau Client
        </Button>
      </div>

      {clients.length === 0 ? (
        <Alert variant="info">Aucun client pour le moment.</Alert>
      ) : (
        <Table striped bordered hover responsive>
          <thead className="table-dark">
            <tr>
              <th>N°</th>
              {/* ✅ Colonnes triables */}
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('nom')}>
                Nom <SortIcon field="nom" sortField={sortField} sortDir={sortDir} />
              </th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('entreprise')}>
                Entreprise <SortIcon field="entreprise" sortField={sortField} sortDir={sortDir} />
              </th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('email')}>
                Email <SortIcon field="email" sortField={sortField} sortDir={sortDir} />
              </th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('telephone')}>
                Téléphone <SortIcon field="telephone" sortField={sortField} sortDir={sortDir} />
              </th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('ville')}>
                Ville <SortIcon field="ville" sortField={sortField} sortDir={sortDir} />
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {/* ✅ sortedClients au lieu de clients */}
            {sortedClients.map((client) => (
              <tr key={client.id}>
                <td>{client.id}</td>
                <td>{client.prenom} {client.nom}</td>
                <td>{client.entreprise || '-'}</td>
                <td>{client.email || '-'}</td>
                <td>{client.telephone || '-'}</td>
                <td>{client.ville || '-'}</td>
                <td>
                  <div className="d-flex gap-1">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => openEditModal(client)}
                    >
                      ✏️
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => openConfirmDelete(client)}
                    >
                      🗑️
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Modal création / édition */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editClient ? '✏️ Modifier le client' : '👤 Nouveau Client'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Nom *</Form.Label>
                  <Form.Control
                    type="text"
                    name="nom"
                    required
                    value={form.nom}
                    onChange={handleField}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Prénom</Form.Label>
                  <Form.Control
                    type="text"
                    name="prenom"
                    value={form.prenom}
                    onChange={handleField}
                  />
                </Form.Group>
              </div>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Entreprise</Form.Label>
              <Form.Control
                type="text"
                name="entreprise"
                value={form.entreprise}
                onChange={handleField}
              />
            </Form.Group>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleField}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Téléphone</Form.Label>
                  <Form.Control
                    type="tel"
                    name="telephone"
                    value={form.telephone}
                    onChange={handleField}
                  />
                </Form.Group>
              </div>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Adresse</Form.Label>
              <Form.Control
                type="text"
                name="adresse"
                value={form.adresse}
                onChange={handleField}
              />
            </Form.Group>
            <div className="row">
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Code postal</Form.Label>
                  <Form.Control
                    type="text"
                    name="code_postal"
                    value={form.code_postal}
                    onChange={handleField}
                  />
                </Form.Group>
              </div>
              <div className="col-md-8">
                <Form.Group className="mb-3">
                  <Form.Label>Ville</Form.Label>
                  <Form.Control
                    type="text"
                    name="ville"
                    value={form.ville}
                    onChange={handleField}
                  />
                </Form.Group>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Annuler
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting
                ? (editClient ? 'Mise à jour...' : 'Création...')
                : (editClient ? 'Mettre à jour' : 'Créer le client')
              }
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal confirmation suppression */}
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>⚠️ Confirmer la suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Supprimer définitivement <strong>{clientToDelete?.prenom} {clientToDelete?.nom}</strong> ?
          {clientToDelete?.entreprise && (
            <div><small className="text-muted">{clientToDelete.entreprise}</small></div>
          )}
          <Alert variant="warning" className="mt-3 mb-0">
            Cette action est irréversible. Le client ne peut pas être supprimé s'il a des chantiers associés.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>
            Annuler
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            🗑️ Supprimer définitivement
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ClientList;